import type { LandIqBoardColumn, LandIqBoardTicket } from '@immo/api-contract';
import type { MoveRefusal } from '@/lib/landiq/move-ticket';

export type MoveToast = {
  variant: 'warning' | 'danger';
  /** Relative to `pages.admin.landiq-data-runs`. */
  key: string;
  values: { stage?: LandIqBoardColumn; entries?: string; started?: number; total?: number };
};

/** A LandIQ refusal as a toast, from tokens only (ADR 0014): `stageEntries` is the target stage's entry count on the card. */
export function moveRefusalToast(
  refusal: MoveRefusal,
  card: { stageEntries: number; ownColumn: LandIqBoardColumn },
): MoveToast {
  switch (refusal.reason) {
    case 'UNKNOWN_TICKET':
      return { variant: 'danger', key: 'board.refusals.UNKNOWN_TICKET', values: {} };
    case 'IMPORTED_NOT_A_TARGET':
      return { variant: 'warning', key: 'board.refusals.IMPORTED_NOT_A_TARGET', values: {} };
    case 'ENVIRONMENT_NOT_ALLOWED':
      return { variant: 'warning', key: 'refusals.ENVIRONMENT_NOT_ALLOWED', values: {} };
    case 'NOT_NEXT_STAGE':
      return refusal.next === null
        ? { variant: 'warning', key: 'board.refusals.NO_NEXT_STAGE', values: {} }
        : { variant: 'warning', key: 'board.refusals.NOT_NEXT_STAGE', values: { stage: refusal.next } };
    case 'STAGE_NOT_DONE':
      return {
        variant: 'warning',
        key: 'board.refusals.STAGE_NOT_DONE',
        values: { stage: refusal.column ?? card.ownColumn },
      };
    case 'BLOCKED':
      return { variant: 'warning', key: 'refusals.BLOCKED', values: { entries: refusal.blockedBy.join(', ') } };
    case 'RUNNER_UNAVAILABLE':
      return refusal.runs.length === 0
        ? { variant: 'danger', key: 'refusals.RUNNER_UNAVAILABLE', values: {} }
        : {
            variant: 'danger',
            key: 'board.refusals.PARTLY_STARTED',
            values: {
              started: refusal.runs.length,
              total: Math.max(refusal.runs.length, card.stageEntries),
            },
          };
  }
}

/** A drop that never reaches LandIQ: the card already carries LandIQ's `next`, so the page applies no rule of its own. */
export function localRefusalToast(card: LandIqBoardTicket, to: LandIqBoardColumn): MoveToast {
  if (to === 'IMPORTED') return { variant: 'warning', key: 'board.refusals.IMPORTED_NOT_A_TARGET', values: {} };

  if (card.next !== null) {
    return { variant: 'warning', key: 'board.refusals.NOT_NEXT_STAGE', values: { stage: card.next } };
  }

  return { variant: 'warning', key: 'board.refusals.NO_NEXT_STAGE', values: {} };
}

/** Renders a toast with the column token turned into its name. Two callers: the board and the run page. */
export function moveToastText(
  t: (key: string, values?: Record<string, string | number>) => string,
  toast: MoveToast,
): string {
  const { stage, ...rest } = toast.values;

  return t(toast.key, { ...rest, ...(stage === undefined ? {} : { stage: t(`board.columns.${stage}`) }) });
}
